<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Pontos de Venda" :on-add="() => router.push({ name: 'create-point-of-sale' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="table"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.active`]="{ item }">
            <span> {{ item.active ? 'Sim' : 'Não' }}</span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span>
              {{ $luxon.DateTime.fromISO(item.created_at).toFormat("dd/MM/yyyy HH:mm:ss") }}
            </span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script setup lang="ts">
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import PointOfSale from '@/types/PointOfSale'
import PointOfSaleService from '@/services/PointOfSaleService'

import { DataTableHeader, DataTableFilter, DefaultDataTableActions } from '@/types/DataTable'
import { useRouter } from 'vue-router'
import { ref, type Ref, useTemplateRef } from 'vue'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import { useLoader } from '@/stores/loader'
import { useAbility } from '@casl/vue'
import { useTextFilter } from '@/helpers/reports/textFilter'

const { can } = useAbility()
const router = useRouter()
const snackbar = useSnackBar()
const dialog = useDialog()
const loader = useLoader()

const table = useTemplateRef<DataTableType>('table')

const headers: DataTableHeader[] = [
  { key: 'id', value: 'id', title: 'ID', sortable: false },
  { key: 'name', value: 'name', title: 'Nome', sortable: false },
  { key: 'active', value: 'active', title: 'Ativo', sortable: false },
  { key: 'created_at', value: 'created_at', title: 'Criado em:', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
]

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({
    label: 'Nome',
    field: 'name',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
])

const resource = 'pos'

const actions: DefaultDataTableActions<PointOfSale> = {
  update: {
    callback: (item: PointOfSale) => router.push({ name: 'edit-point-of-sale', params: { id: item.id } }),
    condition: () => can('update', 'pos'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: PointOfSale) => destroy(item),
    condition: () => can('delete', 'pos'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    return await (new PointOfSaleService()).index(page, filters, per_page)
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os pontos de venda',
      'error',
    )

    throw error
  }
}

async function destroy(item: PointOfSale) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o ponto de venda? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new PointOfSaleService()).destroy(item)

    snackbar.show(
      'Ponto de venda excluído com sucesso.',
      'success',
    )

    if (table.value !== null) {
      table.value.fetch(1)
    }
  } finally {
    loader.decrement()
  }
}
</script>
